import { Account } from '@ambire-common/interfaces/account'
import { Key } from '@ambire-common/interfaces/keystore'

export type PrivateModeReason =
  /** Account already has a Railgun wallet — toggle is enabled. */
  | 'available'
  /** Account has a seed-derived internal key but Railgun wallet hasn't been created yet. */
  | 'creatable'
  /** Engine or wallet creation in progress — show spinner. */
  | 'initializing'
  /** Engine init failed — surface a retry affordance via the tooltip. */
  | 'failed'
  /** Account was imported from a raw private key (no seed phrase available to derive Railgun keys). */
  | 'no-seed'
  /** All associated keys are external (trezor/ledger/lattice/walletconnect). */
  | 'hardware'
  /** Account has no associated signing keys (view-only or unimported). */
  | 'view-only'

/** Subset of `Key` we actually inspect — keeps the helper testable without depending on the full keystore type. */
export type KeyForReason = Pick<Key, 'addr' | 'type'> & {
  meta?: { fromSeedId?: string } & Record<string, unknown>
}

export const computePrivateModeReason = ({
  account,
  keys,
  hasRailgunWallet
}: {
  account: Pick<Account, 'addr' | 'associatedKeys'> | null | undefined
  keys: KeyForReason[]
  hasRailgunWallet: boolean
}): PrivateModeReason => {
  // View-only takes precedence over `hasRailgunWallet`: a watch-only account can
  // never sign, so private mode must stay blocked even if a stale Railgun wallet
  // row is persisted for its address (e.g. the address was previously a full
  // account). Checking this first prevents 'available' from leaking through.
  if (!account) return 'view-only'

  const associated = new Set(account.associatedKeys.map((a) => a.toLowerCase()))
  const accountKeys = keys.filter((k) => associated.has(k.addr.toLowerCase()))

  if (accountKeys.length === 0) return 'view-only'

  // Account has at least one signing key — an already-created Railgun wallet
  // means private mode is ready to use.
  if (hasRailgunWallet) return 'available'

  const hasSeedKey = accountKeys.some(
    (k) => k.type === 'internal' && typeof k.meta?.fromSeedId === 'string'
  )
  if (hasSeedKey) return 'creatable'

  const hasInternalKey = accountKeys.some((k) => k.type === 'internal')
  // Internal key but no seed → user imported a raw private key.
  if (hasInternalKey) return 'no-seed'

  // Only external (hardware / walletconnect) keys remain.
  return 'hardware'
}

/**
 * True when the private wallet can NEVER be created for this account — no retry
 * or setup step will help (raw private-key import, hardware, or view-only). Used
 * to permanently disable the shield/unshield/send entry points. Transient states
 * ('creatable' / 'initializing' / 'failed') are deliberately excluded: private
 * mode becomes available for those, so their entry points stay enabled.
 */
export const isPrivateModeBlocked = (reason: PrivateModeReason): boolean =>
  reason === 'no-seed' || reason === 'hardware' || reason === 'view-only'

export const PRIVATE_MODE_TOOLTIP: Record<
  Exclude<PrivateModeReason, 'available'>,
  string
> = {
  creatable: 'Setting up your private wallet…',
  initializing: 'Setting up your private wallet…',
  failed: "Couldn't set up your private wallet. Click to retry.",
  'no-seed':
    'Private wallet requires a seed phrase. This account was imported from a private key, so a Railgun viewing key cannot be derived.',
  hardware: 'Private wallet is not available for hardware wallets.',
  'view-only':
    'Private wallet is not available for view-only accounts.'
}
